import React, { useState } from 'react'
import PropTypes from 'prop-types'
import {
  ThemeProvider,
  Title,
  Box,
  Text,
  Toasts,
  Button,
  Media,
  Image,
  Level,
  Dialog,
} from '@deseretbook/react-ui'
import { useMediaQuery, usePopup } from '@deseretbook/react-utils'
import MinusCircle from '@deseretbook/icons/react/minus-circle'
import { DeseretBook } from '../../dbcom/api'
import { Logger } from '../../dbcom'

const logger = new Logger({ context: 'account/videos' })

/**
 * Displays "My Videos" that are in user's Deseret Video wishlist
 * @param {array} videos - array of videos
 * @returns {func} Component
 */
const Videos = ({ videos }) => {
  const [showOverlay, setShowOverlay] = useState(false)
  const [thumbnailId, setThumbnailId] = useState()

  // Current video used for actions like removing from a wishlist
  const [activeVideo, setActiveVideo] = useState(null)

  const removeVideoPopup = usePopup()

  // For when the content reaches its mobile breakpoint
  const isMobile = useMediaQuery({ maxWidth: 639 })
  // For when content reaches desktop width
  const isDesktop = useMediaQuery({ minWidth: 1025 })

  /**
   * Show arrow icon overlay on hover
   * @param {event} event - mouse enter event
   */
  const handleMouseEnter = event => {
    setShowOverlay(true)
    setThumbnailId(parseInt(event.currentTarget.id, 10))
  }

  /**
   * Handle mouse leave event
   */
  const handleMouseLeave = () => {
    setShowOverlay(false)
  }

  /**
   * Handle redirect to video product page
   * on mobile so the Media Figure
   * can be clicked as a link
   * @param {string} slug - slug to product page
   */
  const redirectToProductPage = (slug = '') => {
    window.location.href = `/p/${slug}`
  }

  /**
   * Remove video from watchlist
   * @param {object} video - video wished Product
   */
  const removeFromList = async video => {
    try {
      await DeseretBook.wishedProduct(video).delete()

      videos.splice(videos.indexOf(video), 1)
      handleMouseLeave()
      Toasts.success('Successfully removed')
    } catch (message) {
      Toasts.danger('Failed to remove video')
      logger.error(`Unable to remove wishedProduct: "${message}"`)
    }
  }

  return (
    <ThemeProvider>
      <Dialog
        {...removeVideoPopup.popupProps}
        isNegative
        confirmButtonText="Remove"
        cancelButtonText="Keep"
        onConfirm={() => {
          if (activeVideo) {
            removeFromList(activeVideo)
          }
          removeVideoPopup.close()
          setActiveVideo(null)
        }}
        onCancel={() => {
          removeVideoPopup.close()
          setActiveVideo(null)
        }}
      >
        <Text>
          Are you sure you want to remove {activeVideo ? activeVideo.variant.name : ''} from your
          wishlist?
        </Text>
      </Dialog>
      <Title title="My Videos" size={1} />
      {videos.length > 0 &&
        // Mobile layout
        (isMobile ? (
          videos.map(video => (
            <Media>
              <Media.Figure size="large" onClick={() => redirectToProductPage(video.variant.slug)}>
                <Image ratio="16x9" src={video.variant.jwPlayerPosterUrl} alt={video.name} />
              </Media.Figure>
              <Media.Content>
                <Level>
                  <Level.Left>
                    <Level.Item>
                      <a href={`/p/${video.variant.slug}`}>
                        <Text hasEllipsis isBold>
                          {video.variant.name}
                        </Text>
                      </a>
                    </Level.Item>
                  </Level.Left>
                  <Level.Right>
                    <MinusCircle
                      width="2em"
                      height="2em"
                      {...removeVideoPopup.triggerProps}
                      onClick={() => {
                        setActiveVideo(video)
                        removeVideoPopup.open()
                      }}
                    />
                  </Level.Right>
                </Level>
              </Media.Content>
            </Media>
          ))
        ) : (
          // Tablet and larger layout
          <Box justifyContent="start" isWrapped>
            {videos.map(video => (
              // When larger than mobile use thumbnails
              <Box key={video.id}>
                <Box isColumn paddingRight={1} paddingBottom={1}>
                  <Box
                    isColumn
                    id={video.id}
                    style={{
                      backgroundImage: `url(${video.variant.jwPlayerPosterUrl})`,
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }}
                    height={10}
                    minWidth={20}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                  >
                    <Box paddingLeft={1} paddingTop={1} height={4}>
                      <MinusCircle
                        color="white"
                        width="2em"
                        height="2em"
                        {...removeVideoPopup.triggerProps}
                        onClick={() => {
                          setActiveVideo(video)
                          removeVideoPopup.open()
                        }}
                      />
                    </Box>
                    {isDesktop && showOverlay && thumbnailId === video.id && (
                      <Box alignItems="center" justifyContent="center">
                        <Button
                          as="a"
                          href={`/p/${video.variant.slug}`}
                          isRounded
                          color="secondary"
                          icon="arrowRight"
                        />
                      </Box>
                    )}
                  </Box>
                  <Box paddingTop={1}>
                    <a href={`/p/${video.variant.slug}`}>
                      <Text isBold>{video.variant.name}</Text>
                    </a>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        ))}

      {/* No Videos */}
      {videos.length === 0 && (
        <Box justifyContent="center" paddingVertical={10}>
          <Text>You have no videos on your watchlist</Text>
        </Box>
      )}
    </ThemeProvider>
  )
}

Videos.propTypes = {
  videos: PropTypes.arrayOf(PropTypes.shape({})),
}

export default Videos
